import { useState, useCallback } from 'react';
import { TAB_NAMES } from '../../constants';

/**
 * Custom hook for managing tab navigation state and behavior
 *
 * Handles:
 * - Active tab state management
 * - Original tab tracking (for navigation back)
 * - Tab change logic
 * - External tab manipulation for navigation events
 *
 * @param {string} initialTab - Initial tab to set as active
 * @returns {Object} Tab navigation utilities
 */
const useTabNavigation = (initialTab = TAB_NAMES.ALL) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [originalTab, setOriginalTab] = useState(initialTab);

  /**
   * Handle tab changes from user interaction
   * Updates both active and original tab
   */
  const handleTabChange = useCallback((event, tabName) => {
    setActiveTab(tabName);
    setOriginalTab(tabName);
  }, []);

  /**
   * Navigate back to the original tab
   * Used when returning from programmatic navigation
   */
  const handleBackToOriginalTab = useCallback(() => {
    setActiveTab(originalTab);
  }, [originalTab]);

  /**
   * Navigate to a specific tab while preserving original tab
   * Used for programmatic navigation that might need to return
   */
  const navigateToTab = useCallback(
    (tabName) => {
      setOriginalTab(activeTab);
      setActiveTab(tabName);
    },
    [activeTab]
  );

  /**
   * Check if a specific tab is currently active
   */
  const isTabActive = useCallback(
    (tabName) => {
      return activeTab === tabName;
    },
    [activeTab]
  );

  return {
    // State
    activeTab,
    originalTab,

    // Actions
    handleTabChange,
    handleBackToOriginalTab,
    navigateToTab,

    // Utilities
    isTabActive,

    // Direct setters (for advanced use cases)
    setActiveTab,
    setOriginalTab,
  };
};

export default useTabNavigation;
